import { useCallback, useEffect, useRef } from "react";
import { APIProvider, Circle, Map, Polygon, Polyline, useMap } from "@vis.gl/react-google-maps";
import { MapTraceLayer } from "./MapTraceLayer";

export const MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#03080f" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#7fa7bd" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#03080f" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#15314a" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#03080f" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3fd4ff" }, { weight: 1.8 }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#1c6d91" }, { weight: 1.3 }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#143b52" }, { weight: 0.9 }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#7fa7bd" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#7fa7bd" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#091d2c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#7fa7bd" }] },
];
const COLORS = ["#3fd4ff", "#52dba9", "#ffb547", "#fa91ae", "#77b8f0", "#d6d87d"];
const zoneColor = (zone) => zone.salesperson_id ? COLORS[[...zone.salesperson_id].reduce((sum, c) => sum + c.charCodeAt(0), 0) % COLORS.length] : "#77828c";
const markerColor = (venue) => venue.in_negotiation ? "#ffb547" :
  !venue.salesperson_name ? "#3fd4ff" : "#52dba9";
const middle = (polygon) => ({ lat: polygon.reduce((sum, p) => sum + p.lat, 0) / polygon.length,
                               lng: polygon.reduce((sum, p) => sum + p.lng, 0) / polygon.length });

const MapEvents = ({ onBounds, onZoom, onMapReady, onTilesReady }) => {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    const update = () => {
      const bounds = map.getBounds();
      if (bounds && map.getProjection()) { onMapReady(map);
        const southWest = bounds.getSouthWest(); const northEast = bounds.getNorthEast();
        onBounds({ south: southWest.lat(), west: southWest.lng(), north: northEast.lat(), east: northEast.lng() }); }
      onZoom(map.getZoom() || 10);
    };
    const idle = map.addListener("idle", update);
    const tiles = map.addListener("tilesloaded", () => onTilesReady(true));
    update();
    return () => { idle.remove(); tiles.remove(); onMapReady(null); };
  }, [map, onBounds, onZoom, onMapReady, onTilesReady]);
  return null;
};

const MapFocus = ({ focus }) => {
  const map = useMap();
  useEffect(() => {
    if (map && focus?.center) { map.panTo(focus.center); map.setZoom(focus.zoom); }
  }, [map, focus]);
  return null;
};

const MapLabels = ({ zones, venues, zoom, onSelectZone, onOpenVenue }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps) return undefined;
    const overlays = [];
    const attach = (position, content, type, onClick) => {
      const google = window.google;
      class FloatingLabel extends google.maps.OverlayView {
        onAdd() {
          this.node = document.createElement("button");
          this.node.type = "button";
          this.node.className = type === "zone" ? "map-zone-label" : "map-venue-marker";
          this.node.setAttribute("aria-label", content.label);
          this.node.setAttribute("data-testid", `${type === "zone" ? "map-zone" : "map-venue"}-${content.id}`);
          this.node.style.setProperty("--pin-color", content.color);
          const title = document.createElement("span");
          title.className = type === "zone" ? "zone-label-text" : "venue-pin";
          title.textContent = content.title;
          this.node.appendChild(title);
          if (content.extra) {
            const extra = document.createElement("span");
            extra.className = "venue-pin-label";
            extra.textContent = content.extra;
            this.node.appendChild(extra);
          }
          this.node.addEventListener("click", (event) => { event.stopPropagation(); onClick(); });
          this.getPanes().overlayMouseTarget.appendChild(this.node);
        }
        draw() {
          const point = this.getProjection()?.fromLatLngToDivPixel(new google.maps.LatLng(position));
          if (this.node && point) { this.node.style.left = `${point.x}px`; this.node.style.top = `${point.y}px`; }
        }
        onRemove() { this.node?.remove(); }
      }
      const overlay = new FloatingLabel();
      overlay.setMap(map); overlays.push(overlay);
    };
    zones.forEach((zone) => attach(middle(zone.polygon), {
      id: zone.id, label: `Zona ${zone.name}`, title: zone.name, color: zoneColor(zone),
    }, "zone", () => onSelectZone(zone.id)));
    venues.forEach((venue) => attach(venue.coordinates, {
      id: venue.id, label: `Apri ${venue.name}`, title: (venue.rating || 0) >= 3 ? "★" : "•",
      color: markerColor(venue),
      extra: zoom >= 15 ? `${venue.name} · ${venue.rating != null ? `★ ${venue.rating}` : "—"} · ${venue.salesperson_name || "Da assegnare"}` : null,
    }, "venue", () => onOpenVenue(venue.id)));
    return () => overlays.forEach((overlay) => overlay.setMap(null));
  }, [map, zones, venues, zoom, onSelectZone, onOpenVenue]);
  return null;
};

export const SearchMap = ({ mapsKey, zones, venues, selectedZoneId, drawing, points,
                            onBeginStroke, onCompleteStroke, onShortStroke, onBounds, onZoom, onReady, zoom,
                            onSelectZone, onOpenVenue, onError, onTilesReady, circle, onCircleChange, focus }) => {
  const mapRef = useRef(null);
  const onMapReady = useCallback((map) => { mapRef.current = map; onReady(Boolean(map)); }, [onReady]);
  return <div className={`search-map${drawing ? " drawing" : ""}`} data-testid="search-map">
    <APIProvider apiKey={mapsKey} onError={onError}>
      <Map defaultCenter={{ lat: 41.4676, lng: 12.9037 }} defaultZoom={10} minZoom={8}
        styles={MAP_STYLE} disableDefaultUI disableDoubleClickZoom={drawing} draggable={!drawing}
        gestureHandling={drawing ? "none" : "greedy"} clickableIcons={false}
        keyboardShortcuts={false} style={{ width: "100%", height: "100%" }}>
        <MapEvents onBounds={onBounds} onZoom={onZoom} onMapReady={onMapReady} onTilesReady={onTilesReady} />
        <MapFocus focus={focus} />
        {circle && !drawing && <Circle center={circle.center} radius={circle.radius_km * 1000}
          fillColor="#ffb547" fillOpacity={.16} strokeColor="#ffb547" strokeWeight={2}
          editable draggable onCenterChanged={(center) => { if (center &&
            (Math.abs(center.lat() - circle.center.lat) > .00001 || Math.abs(center.lng() - circle.center.lng) > .00001))
            onCircleChange({ ...circle, center: { lat: center.lat(), lng: center.lng() } }); }}
          onRadiusChanged={(radius) => { const km = Math.round(radius / 1000);
            if (km >= 1 && km <= 50 && km !== circle.radius_km) onCircleChange({ ...circle, radius_km: km }); }} />}
        {zones.map((zone) => <Polygon key={zone.id} paths={zone.polygon}
          fillColor={selectedZoneId === zone.id ? "#ffb547" : zoneColor(zone)}
          strokeColor={selectedZoneId === zone.id ? "#ffb547" : zoneColor(zone)}
          fillOpacity={selectedZoneId === zone.id ? .27 : .16} strokeOpacity={.85} strokeWeight={2}
          clickable={!drawing}
          onClick={() => onSelectZone(zone.id)} />)}
        {points.length > 1 && <Polyline path={points} strokeColor="#ffb547" strokeWeight={3} />}
        {points.length >= 3 && <Polygon paths={points} fillColor="#ffb547" fillOpacity={.16}
          strokeColor="#ffb547" strokeWeight={2} />}
        <MapLabels zones={drawing ? [] : zones} venues={drawing ? [] : venues} zoom={zoom} onSelectZone={onSelectZone} onOpenVenue={onOpenVenue} />
      </Map>
    </APIProvider>
    {drawing && <MapTraceLayer mapRef={mapRef} onBegin={onBeginStroke}
      onComplete={onCompleteStroke} onShortStroke={onShortStroke} />}
  </div>;
};